import { Fragment, useMemo } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrencyFractionDigits } from '@/hooks/useCurrencyFractionDigits';
import { minorUnitStep } from '@/utils/money';

interface MoneyInputProps {
    /** The numeric value as a string for controlled input. */
    value: string;
    /** The selected currency code (e.g., "USD"). */
    currency: string;
    /** Callback when the numeric value changes. */
    onValueChange: (value: string) => void;
    /** Callback when the currency changes. */
    onCurrencyChange: (currency: string) => void;
    /** Optional label displayed above the input. */
    label?: string;
    /** Error message to display (turns input red). */
    error?: string;
    /** Helper text displayed below the input. */
    helperText?: string;
    /** Whether the input is disabled. */
    disabled?: boolean;
    /** Placeholder text for the value input. */
    placeholder?: string;
    /** Optional step override. By default it is derived from the selected currency. */
    step?: string;
}

/**
 * Combined money input component with value and currency in a single field.
 * Currencies are sourced from user settings (settings.currencies).
 *
 * Visual layout:
 * ┌─────────────────────┬────────────┐
 * │ 421.00              │ USD ▼      │
 * └─────────────────────┴────────────┘
 */
export function MoneyInput({
    value,
    currency,
    onValueChange,
    onCurrencyChange,
    label,
    error,
    helperText,
    disabled = false,
    placeholder = '0.00',
    step,
}: MoneyInputProps) {
    const { settings } = useAuth();
    const fractionDigits = useCurrencyFractionDigits(currency);
    const effectiveStep = step ?? minorUnitStep(fractionDigits);

    // Get currency options from user settings
    const currencyOptions = useMemo(() => {
        const currencies = settings?.currencies || ['USD'];
        const options = currencies.map((code) => ({
            value: code,
            label: code,
        }));

        // Ensure the current currency is in the list (edge case: user had a currency that was later removed from their settings)
        if (currency && !currencies.includes(currency)) {
            options.unshift({ value: currency, label: currency });
        }

        return options;
    }, [settings?.currencies, currency]);

    const selectedOption = currencyOptions.find((opt) => opt.value === currency);
    const showCurrencyDropdown = currencyOptions.length > 1;

    const borderClasses = error
        ? 'border-red-500 focus-within:ring-red-500'
        : 'border-gray-300 dark:border-gray-600 focus-within:ring-blue-500';

    return (
        <div className="w-full">
            {label && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {label}
                </label>
            )}
            <div className="relative flex max-w-full">
                <div
                    className={`
                        flex flex-1 rounded-lg border overflow-hidden
                        focus-within:ring-2
                        bg-white dark:bg-gray-800
                        ${borderClasses}
                        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                >
                    {/* Value input */}
                    <input
                        type="number"
                        inputMode="decimal"
                        step={effectiveStep}
                        value={value}
                        onChange={(e) => onValueChange(e.target.value)}
                        disabled={disabled}
                        placeholder={placeholder}
                        className={`
                            flex-1 min-w-0 px-4 py-2 text-base
                            border-none focus:outline-hidden focus:ring-0
                            bg-transparent
                            ${showCurrencyDropdown ? 'rounded-l-lg' : 'rounded-lg'}
                            text-gray-900 dark:text-gray-100
                            placeholder-gray-400 dark:placeholder-gray-500
                            disabled:cursor-not-allowed
                            [appearance:textfield]
                            [&::-webkit-outer-spin-button]:appearance-none
                            [&::-webkit-inner-spin-button]:appearance-none
                        `}
                    />

                    {/* Currency dropdown - hidden when only one currency */}
                    {showCurrencyDropdown && (
                        <Listbox value={currency} onChange={onCurrencyChange} disabled={disabled}>
                            <Listbox.Button
                                tabIndex={0}
                                className={`
                                    flex items-center gap-1 px-3 py-2
                                    border-l border-gray-300 dark:border-gray-600
                                    bg-gray-50 dark:bg-gray-700
                                    text-gray-700 dark:text-gray-200
                                    hover:bg-gray-100 dark:hover:bg-gray-600
                                    focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-blue-500
                                    rounded-r-lg
                                    disabled:cursor-not-allowed disabled:hover:bg-gray-50 dark:disabled:hover:bg-gray-700
                                    min-w-[80px] justify-between
                                `}
                            >
                                <span className="font-medium">
                                    {selectedOption?.label || currency}
                                </span>
                                <ChevronDownIcon
                                    className="h-5 w-5 text-gray-600 dark:text-gray-300 shrink-0"
                                    aria-hidden="true"
                                />
                            </Listbox.Button>

                            {/* Dropdown options - positioned outside the overflow container */}
                            <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <Listbox.Options
                                    className="absolute right-0 top-full z-50 mt-1 max-h-60 w-auto min-w-[100px] overflow-auto rounded-lg bg-white dark:bg-dark shadow-lg ring-1 ring-black/5 dark:ring-gray-600 focus:outline-hidden"
                                >
                                    {currencyOptions.map((option, index) => (
                                        <Listbox.Option
                                            key={option.value}
                                            value={option.value}
                                            className={({ active }) =>
                                                `relative cursor-pointer select-none py-2 pl-4 pr-4 ${
                                                    active
                                                        ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                                                        : 'text-gray-900 dark:text-gray-100'
                                                } ${index === 0 ? 'rounded-t-lg' : ''} ${index === currencyOptions.length - 1 ? 'rounded-b-lg' : ''}`
                                            }
                                        >
                                            {({ selected }) => (
                                                <span
                                                    className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                                >
                                                    {option.label}
                                                </span>
                                            )}
                                        </Listbox.Option>
                                    ))}
                                </Listbox.Options>
                            </Transition>
                        </Listbox>
                    )}
                </div>
            </div>
            {error && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
            {helperText && !error && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {helperText}
                </p>
            )}
        </div>
    );
}
